import type {SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType, PostState} from '@Components/social-media/post.vo';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {doesAccountNeedReconnection} from '@Components/social-media/account.vo';
import {PILL_THEME} from '@Components/pill-legacy';
import {assertNever} from '@Utils/assert-never.util';

export enum PostDisplayStatusKind {
  DID_NOT_POST = 'DID_NOT_POST',
  PARTIAL = 'PARTIAL',
  RESOLVED = 'RESOLVED',
  OTHER = 'OTHER', // fall through to existing getReadableState/getPillThemeByState
}

export enum PostErrorReason {
  CONNECTION_EXPIRED = 'CONNECTION_EXPIRED',
  DID_NOT_POST = 'DID_NOT_POST',
  SERVER_ERROR = 'SERVER_ERROR',
}

export interface FailedPublishSlot {
  account: SocialAccount;
  mediaType: PlatformMediaType;
  publishErrorMessage: string | null;
}

export interface PostErrorTarget {
  account: SocialAccount;
  mediaType: PlatformMediaType;
  reason: PostErrorReason;
}

export interface PostErrorAccount {
  account: SocialAccount;
  reason: PostErrorReason;
}

export interface PostDisplayStatus {
  kind: PostDisplayStatusKind;
  label: string;
  theme: PILL_THEME;
  failedCount: number;
  totalCount: number;
  publishedCount: number;
}

interface PublishSlot {
  account: SocialAccount;
  mediaType: PlatformMediaType;
  state: PostState;
  publishErrorMessage: string | null;
}

const MEDIA_TYPES: PlatformMediaType[] = [PlatformMediaType.POST, PlatformMediaType.STORIES, PlatformMediaType.REELS];
const SERVER_ERROR_SIGNATURES: string[] = ['server error', 'try again', 'temporarily', 'timeout', 'timed out'];

const collectSlots = (post: SocialPost): PublishSlot[] => {
  const slots: PublishSlot[] = [];
  post.accounts.forEach((account) => {
    const params = post.publishingParams[account.id];
    if (!params) {
      return;
    }
    MEDIA_TYPES.forEach((mediaType) => {
      const slot = params[mediaType];
      if (!slot) {
        return;
      }
      slots.push({account, mediaType, state: slot.state, publishErrorMessage: slot.publishErrorMessage});
    });
  });
  return slots;
};

const isFailedSlot = (slot: PublishSlot): boolean => {
  return slot.state === PostState.ERROR || slot.state === PostState.SCHEDULE_ERROR;
};

const isPublishedSlot = (slot: PublishSlot): boolean => {
  return slot.state === PostState.PUBLISHED;
};

export const getFailedPublishSlots = (post: SocialPost): FailedPublishSlot[] => {
  return collectSlots(post)
    .filter(isFailedSlot)
    .map((slot) => ({account: slot.account, mediaType: slot.mediaType, publishErrorMessage: slot.publishErrorMessage}));
};

const getReasonForAccount = (account: SocialAccount, publishErrorMessage: string | null): PostErrorReason => {
  if (doesAccountNeedReconnection(account)) {
    return PostErrorReason.CONNECTION_EXPIRED;
  }
  const message = (publishErrorMessage ?? '').toLowerCase();
  if (SERVER_ERROR_SIGNATURES.some((signature) => message.includes(signature))) {
    return PostErrorReason.SERVER_ERROR;
  }
  return PostErrorReason.DID_NOT_POST;
};

export const getPostErrorTargets = (post: SocialPost): PostErrorTarget[] => {
  return getFailedPublishSlots(post).map((slot) => ({
    account: slot.account,
    mediaType: slot.mediaType,
    reason: getReasonForAccount(slot.account, slot.publishErrorMessage),
  }));
};

export const getPostErrorAccounts = (post: SocialPost): PostErrorAccount[] => {
  const errorAccounts: PostErrorAccount[] = [];
  const indexByAccountId = new Map<number, number>();

  getPostErrorTargets(post).forEach((target) => {
    const existingIndex = indexByAccountId.get(target.account.id);
    if (existingIndex === undefined) {
      indexByAccountId.set(target.account.id, errorAccounts.length);
      errorAccounts.push({account: target.account, reason: target.reason});
      return;
    }
    if (target.reason === PostErrorReason.CONNECTION_EXPIRED) {
      errorAccounts[existingIndex] = {...errorAccounts[existingIndex], reason: PostErrorReason.CONNECTION_EXPIRED};
    }
  });

  return errorAccounts;
};

export const getPostFailedAccounts = (post: SocialPost): SocialAccount[] => {
  return getPostErrorAccounts(post).map((errorAccount) => errorAccount.account);
};

/** "Fix this" only reconnects accounts that need it; when a healthy account's retry fails (server/rate-limit error), telling the user to reconnect is wrong — surface a plain publish-failed message instead. */
export const getFixFailedToastMessage = (accounts: SocialAccount[], accountId: number): string => {
  const account = accounts.find((candidate) => candidate.id === accountId);

  if (account && doesAccountNeedReconnection(account)) {
    return window.i18next.t('pmwjs_social_growl_fix_failed');
  }

  return window.i18next.t('pmwjs_social_growl_publish_failed');
};

export const getPostErrorReasonLabel = (reason: PostErrorReason): string => {
  switch (reason) {
    case PostErrorReason.CONNECTION_EXPIRED:
      return window.i18next.t('pmwjs_social_error_reason_connection_expired');
    case PostErrorReason.DID_NOT_POST:
      return window.i18next.t('pmwjs_social_error_reason_did_not_post');
    case PostErrorReason.SERVER_ERROR:
      return window.i18next.t('pmwjs_social_error_reason_server_error');
    default:
      return assertNever(reason);
  }
};

/**
 * Error-reason label for one account's specific publishing slot (account + media type), for use as an
 * account-selector subtitle. Returns null when that slot isn't errored, so callers can fall back to the
 * platform-name subtitle.
 */
export const getErrorSubtitleForAccountSlot = (post: SocialPost, account: SocialAccount, mediaType: PlatformMediaType): string | null => {
  const target = getPostErrorTargets(post).find((errorTarget) => errorTarget.account.id === account.id && errorTarget.mediaType === mediaType);
  return target ? getPostErrorReasonLabel(target.reason) : null;
};

const buildOtherStatus = (): PostDisplayStatus => ({
  kind: PostDisplayStatusKind.OTHER,
  label: '',
  theme: PILL_THEME.NEUTRAL,
  failedCount: 0,
  totalCount: 0,
  publishedCount: 0,
});

export const getPostDisplayStatus = (post: SocialPost, opts?: {justResolved?: boolean}): PostDisplayStatus => {
  const slots = collectSlots(post);
  const totalCount = slots.length;
  const failedCount = slots.filter(isFailedSlot).length;
  const publishedCount = slots.filter(isPublishedSlot).length;

  if (opts?.justResolved && failedCount === 0 && totalCount > 0) {
    return {
      kind: PostDisplayStatusKind.RESOLVED,
      theme: PILL_THEME.SUCCESS,
      failedCount: 0,
      totalCount,
      publishedCount,
      label: window.i18next.t('pmwjs_social_post_n_of_m_published', {published: publishedCount, total: totalCount}),
    };
  }

  const isFullyFailed = post.state === PostState.ERROR || post.state === PostState.SCHEDULE_ERROR || (totalCount > 0 && failedCount === totalCount);
  if (isFullyFailed) {
    return {
      kind: PostDisplayStatusKind.DID_NOT_POST,
      theme: PILL_THEME.DANGER,
      failedCount,
      totalCount,
      publishedCount,
      label: window.i18next.t('pmwjs_social_post_did_not_post'),
    };
  }

  if (post.state === PostState.PUBLISHED && failedCount > 0) {
    return {
      kind: PostDisplayStatusKind.PARTIAL,
      theme: PILL_THEME.SUCCESS,
      failedCount,
      totalCount,
      publishedCount,
      label: window.i18next.t('pmwjs_social_post_n_of_m_published', {published: publishedCount, total: totalCount}),
    };
  }

  return buildOtherStatus();
};

export const compareByReconnectFirst = (a: SocialAccount, b: SocialAccount): number => {
  return Number(doesAccountNeedReconnection(b)) - Number(doesAccountNeedReconnection(a));
};
